import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

// Auto-saves AG Grid row changes back to the server. Each row keeps an
// immutable id; persisted rows additionally have a database recordId.
export default class extends Controller {
  static targets = ["status"]

  static values = {
    createUrl: String,
    updateUrlTemplate: String,
    delay: { type: Number, default: 500 },
    fieldMap: Object,
    paramNamespace: String
  }

  connect() {
    this.timers = {}
    this.saving = new Set()
    this.createQueue = Promise.resolve()
    this.pendingCreates = {}
  }

  disconnect() {
    Object.values(this.timers).forEach(clearTimeout)
    this.timers = {}

    if (this.statusTimeout) {
      clearTimeout(this.statusTimeout)
    }
  }

  cellChanged({ detail: { data } }) {
    if (!data?.id || this.isSystemRow(data.id)) return

    this.debounceSave(data)
  }

  debounceSave(data) {
    const rowId = String(data.id)

    if (!data.recordId) {
      this.debounceCreate(rowId, data)
      return
    }

    this.debounceUpdate(rowId, data, data.recordId)
  }

  debounceUpdate(rowId, data, recordId) {
    clearTimeout(this.timers[rowId])

    this.timers[rowId] = setTimeout(() => {
      delete this.timers[rowId]
      this.persistRow(rowId, data, recordId)
    }, this.delayValue)
  }

  debounceCreate(rowId, data) {
    if (!this.hasAnyData(data)) return

    let pending = this.pendingCreates[rowId]

    if (!pending) {
      let markReady
      const ready = new Promise((resolve) => { markReady = resolve })
      pending = { data, ready, markReady, started: false, dirty: false }
      this.pendingCreates[rowId] = pending

      this.createQueue = this.createQueue
        .then(() => pending.ready)
        .then(() => {
          pending.started = true
          return this.persistRow(rowId, pending.data, undefined)
        })
        .then((recordId) => {
          pending.recordId = recordId
        })
        .finally(() => {
          delete this.pendingCreates[rowId]
          // Later edits may have scheduled another create debounce after this one started.
          clearTimeout(this.timers[rowId])
          delete this.timers[rowId]

          // Persist edits made while the POST was in flight using the database
          // ID adopted from that POST's response header.
          if (pending.dirty && pending.recordId) {
            this.debounceUpdate(rowId, pending.data, pending.recordId)
          }
        })
    } else {
      pending.data = data
      if (pending.started) pending.dirty = true
    }

    clearTimeout(this.timers[rowId])
    this.timers[rowId] = setTimeout(() => {
      delete this.timers[rowId]
      pending.markReady()
    }, this.delayValue)
  }

  async persistRow(rowId, data, recordId = data.recordId) {
    if (this.saving.has(rowId)) {
      if (recordId) {
        this.debounceUpdate(rowId, data, recordId)
      } else {
        this.debounceSave(data)
      }
      return
    }

    this.saving.add(rowId)
    this.showStatus("saving")

    try {
      const response = await this.submitRow(recordId, data)

      if (response.ok) {
        if (!recordId) return this.handleCreateSuccess(rowId, response)

        this.handleSaveSuccess(rowId, response)
        return recordId
      } else {
        this.handleSaveFailure(rowId, response)
      }
    } catch (error) {
      console.error("Grid autosave error:", error)
      this.showStatus("error")
    } finally {
      this.saving.delete(rowId)
    }
  }

  async submitRow(recordId, data) {
    const response = await fetch(this.urlFor(recordId), {
      method: this.methodFor(recordId),
      headers: this.requestHeaders,
      body: this.buildFormData(data)
    })

    return {
      ok: response.ok,
      status: response.status,
      contentType: response.headers.get("Content-Type") || "",
      recordId: response.headers.get("X-Grid-Record-Id"),
      text: await response.text()
    }
  }

  handleCreateSuccess(rowId, response) {
    const recordId = response.recordId?.trim()

    if (!recordId) {
      console.error(`Grid create returned no X-Grid-Record-Id for row ${rowId}`)
      this.showStatus("error")
      return
    }

    this.handleSaveSuccess(rowId, response)
    return recordId
  }

  handleSaveSuccess(rowId, response) {
    if (response.text && response.contentType.includes("turbo-stream")) {
      Turbo.renderStreamMessage(response.text)
    }

    this.clearRowErrors(rowId)
    this.showStatus("saved")
  }

  handleSaveFailure(rowId, response) {
    console.error(`Grid autosave failed (${response.status}) for row ${rowId}`)

    if (response.status === 422) {
      this.handleValidationErrors(rowId, response.text)
    }

    this.showStatus("error")
  }

  buildFormData(data) {
    const formData = new FormData()

    formData.append("grid_row_id", data.id)

    for (const [gridField, railsParam] of Object.entries(this.fieldMapValue)) {
      const value = data[gridField]
      if (value !== undefined && value !== null) {
        formData.append(`${this.paramNamespaceValue}[${railsParam}]`, value)
      }
    }

    return formData
  }

  handleValidationErrors(rowId, bodyText) {
    try {
      const errors = JSON.parse(bodyText).errors
      if (!errors || typeof errors !== "object") return

      const gridController = this.gridController
      if (!gridController) return

      const mappedErrors = {}
      for (const [railsField, messages] of Object.entries(errors)) {
        const gridField = this.reverseFieldMap[railsField]
        if (gridField && Array.isArray(messages) && messages[0]) {
          mappedErrors[gridField] = messages[0]
        }
      }

      if (Object.keys(mappedErrors).length > 0) {
        gridController.setRowErrors(rowId, mappedErrors)
      }
    } catch (error) {
      console.warn("Grid autosave: could not parse validation errors", error)
    }
  }

  clearRowErrors(rowId) {
    if (!this.gridController) return

    this.gridController.setRowErrors(
      rowId,
      Object.fromEntries(Object.keys(this.fieldMapValue).map((field) => [field, null]))
    )
  }

  isSystemRow(rowId) {
    return String(rowId).startsWith("__")
  }

  hasAnyData(data) {
    return Object.keys(this.fieldMapValue).some((field) => {
      const value = data[field]
      return value !== undefined && value !== null && String(value).trim() !== ""
    })
  }

  showStatus(state) {
    if (!this.hasStatusTarget) return

    const states = {
      saving: { text: "Saving…", className: "text-xs text-base-content/50" },
      saved: { text: "Saved", className: "text-xs text-success", clearAfter: 2000 },
      error: { text: "Save failed", className: "text-xs text-error", clearAfter: 4000 }
    }

    const config = states[state]
    if (!config) return

    clearTimeout(this.statusTimeout)
    this.statusTarget.textContent = config.text
    this.statusTarget.className = config.className

    if (config.clearAfter) {
      this.statusTimeout = setTimeout(() => {
        this.statusTarget.textContent = ""
      }, config.clearAfter)
    }
  }

  urlFor(recordId) {
    return recordId
      ? this.updateUrlTemplateValue.replace(":id", recordId)
      : this.createUrlValue
  }

  methodFor(recordId) {
    return recordId ? "PATCH" : "POST"
  }

  get requestHeaders() {
    return {
      "X-CSRF-Token": this.csrfToken,
      Accept: "text/vnd.turbo-stream.html"
    }
  }

  get gridController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "grid")
  }

  get reverseFieldMap() {
    return Object.fromEntries(
      Object.entries(this.fieldMapValue).map(([gridField, railsParam]) => [railsParam, gridField])
    )
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
